import React from "react";
import ServiceLayout from "../components/ServiceLayout";

const EventCatering = () => {
  const whatsIncluded = [
    {
      title: "Menu Design Consultation",
      description: "Bespoke menu curation based on event theme, guest preferences, and dietary requirements."
    },
    {
      title: "Live Stations",
      description: "Interactive food counters including pasta bars, chaat stations, and dessert live counters."
    },
    {
      title: "Full Service Staff",
      description: "Trained captains, stewards, bartenders, and buffet attendants in uniform."
    },
    {
      title: "Bar Partners",
      description: "Licensed bar service with certified mixologists and premium beverage selection."
    },
    {
      title: "Setup & Breakdown",
      description: "Complete event setup including linens, tableware, and décor coordination."
    },
    {
      title: "On-Site Coordinator",
      description: "Dedicated event manager to oversee service flow and guest experience."
    }
  ];

  const perfectFor = [
    "Annual award nights and galas",
    "Product launches and brand events",
    "Board dinners and investor meetings",
    "Team off-sites and retreats",
    "Wedding receptions and celebrations",
    "Conference networking dinners"
  ];

  const features = [
    {
      title: "Award Nights",
      description: "Black-tie plated service with premium menus",
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1E3A5F" strokeWidth="1.5"><path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/></svg>
    },
    {
      title: "Conferences",
      description: "Full-day catering with multiple meal breaks",
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1E3A5F" strokeWidth="1.5"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M8 2L8 6M16 2L16 6M3 10L21 10"/></svg>
    },
    {
      title: "Gala Dinners",
      description: "Multi-course experiences with wine pairing",
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1E3A5F" strokeWidth="1.5"><path d="M8 21L12 13L16 21M12 13V3M12 3L8 7M12 3L16 7"/></svg>
    },
    {
      title: "Cocktail Parties",
      description: "Canapés, finger foods, and premium bar service",
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1E3A5F" strokeWidth="1.5"><path d="M9 21H15M12 17V21M7 3H17L12 17L7 3Z"/></svg>
    }
  ];

  const faqs = [
    { question: 'What types of corporate events do you cater?', answer: 'We cater workplace events such as conferences, meetings, team celebrations, award nights, business gatherings and other company events.' },
    { question: 'Can the corporate event menu be customized?', answer: 'Yes. Menus can be planned around the event format, guest profile, dietary requirements, service style and budget.' },
    { question: 'Do you provide corporate catering across Bangalore?', answer: 'Yes. We serve corporate event requirements across Bengaluru, subject to venue, guest count and schedule.' }
  ];

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": 'Corporate Event Catering in Bangalore',
      "serviceType": 'Corporate event catering',
      "url": 'https://theofficemealco.com/event-catering',
      "provider": { "@type": "Organization", "name": "The Office Meal Co.", "url": "https://theofficemealco.com/" },
      "areaServed": { "@type": "City", "name": "Bengaluru" }
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map((faq) => ({
        "@type": "Question", "name": faq.question,
        "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
      }))
    }
  ];

  return (
    <ServiceLayout
      title="Corporate Event Catering in Bangalore"
      subtitle="Corporate catering for office events, conferences, team celebrations, meetings, award nights and business gatherings across Bangalore."
      heroImage="/images/event-catering.png"
      whatsIncluded={whatsIncluded}
      perfectFor={perfectFor}
      seoTitle="Corporate Catering & Event Catering Bangalore"
      seoDescription="Corporate event catering in Bangalore for conferences, team celebrations, award nights, meetings and office events with customised menus and service."
      seoKeywords="corporate event catering bangalore, office event catering, corporate catering bangalore, conference catering"
      canonicalUrl="https://theofficemealco.com/event-catering"
      structuredData={structuredData}
      features={features}
    >
      {/* Custom content */}
      <div className="mb-16">
        <span className="text-[#CBA365] text-xs tracking-[0.24em] uppercase font-medium mb-4 block font-sans">
           • Event Types
        </span>
        <h2 className="font-serif text-3xl text-[#1E3A5F] mb-8">
          Corporate catering for every workplace occasion
        </h2>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="relative group overflow-hidden">
            <img 
              src="/images/award-night.webp" 
              alt="Award Night" 
              className="w-full h-[300px] object-cover transition-transform group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1E3A5F] to-transparent opacity-80"></div>
            <div className="absolute bottom-0 left-0 p-6">
              <h3 className="font-serif text-xl text-[#FDFBF7] mb-2">Award Nights</h3>
              <p className="text-sm text-[#E8DDCB] font-sans">Plated service with champagne reception</p>
            </div>
          </div>

          <div className="relative group overflow-hidden">
            <img 
              src="/images/conference.png" 
              alt="Conference" 
              className="w-full h-[300px] object-cover transition-transform group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1E3A5F] to-transparent opacity-80"></div>
            <div className="absolute bottom-0 left-0 p-6">
              <h3 className="font-serif text-xl text-[#FDFBF7] mb-2">Conferences</h3>
              <p className="text-sm text-[#E8DDCB] font-sans">Full-day catering with refreshment breaks</p>
            </div>
          </div>

          <div className="relative group overflow-hidden">
            <img 
              src="/images/gala-dinner.webp" 
              alt="Gala Dinner" 
              className="w-full h-[300px] object-cover transition-transform group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1E3A5F] to-transparent opacity-80"></div>
            <div className="absolute bottom-0 left-0 p-6">
              <h3 className="font-serif text-xl text-[#FDFBF7] mb-2">Gala Dinners</h3>
              <p className="text-sm text-[#E8DDCB] font-sans">Multi-course with wine pairing</p>
            </div>
          </div>
        </div>
      </div>

      {/* Process */}
      <div className="bg-[#F3EFE6] p-8 md:p-12">
        <h3 className="font-serif text-2xl text-[#1E3A5F] mb-8 text-center">How our corporate event catering process works</h3>
        <div className="grid md:grid-cols-4 gap-6">
          {[
            { step: "01", title: "Consultation", desc: "Understand your vision and requirements" },
            { step: "02", title: "Menu Design", desc: "Bespoke menu curation and tasting" },
            { step: "03", title: "Execution", desc: "Flawless on-site service delivery" },
            { step: "04", title: "Feedback", desc: "Post-event review and continuous improvement" }
          ].map((item) => (
            <div key={item.step} className="text-center">
              <span className="font-serif text-3xl text-[#CBA365] block mb-2">{item.step}</span>
              <h4 className="font-medium text-[#1E3A5F] mb-1 font-sans">{item.title}</h4>
              <p className="text-sm text-[#64748B] font-sans">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-16 bg-[#F3EFE6] p-8 md:p-12">
        <h2 className="font-serif text-3xl text-[#1E3A5F] mb-8">Frequently asked questions</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {faqs.map((faq) => (
            <div key={faq.question} className="bg-white p-6 border border-[#E8DDCB]">
              <h3 className="font-medium text-[#1E3A5F] mb-3 font-sans">{faq.question}</h3>
              <p className="text-sm text-[#64748B] leading-relaxed font-sans">{faq.answer}</p>
            </div>
          ))}
        </div>
      </div>
    </ServiceLayout>
  );
};

export default EventCatering;
